/**
 * Debug Logger Utility
 * 
 * This utility provides easy access to the admin debug panel logging system.
 * It can be used throughout the application to log events, API calls, user actions, etc.
 */

class DebugLogger {
  constructor() {
    this.isEnabled = true;
  }

  // Check if debug system is available
  isAvailable() {
    return typeof window !== 'undefined' && window.adminDebug;
  }

  // Add a log entry
  log(message, type = 'info', data = null) {
    if (!this.isEnabled || !this.isAvailable()) return;
    
    try {
      window.adminDebug.addLog(message, type, data);
    } catch (error) {
      console.warn('Debug logging failed:', error);
    }
  }

  // Convenience methods for different log types
  info(message, data = null) {
    this.log(message, 'info', data);
  }

  warning(message, data = null) {
    this.log(message, 'warning', data);
  }

  error(message, data = null) {
    this.log(message, 'error', data);
  }

  system(message, data = null) {
    this.log(message, 'system', data);
  }

  api(message, data = null) {
    this.log(message, 'api', data);
  }

  user(message, data = null) {
    this.log(message, 'user', data);
  }

  // Log API calls with detailed information
  logApiCall(url, method, request = null, response = null, duration = null) {
    if (!this.isEnabled || !this.isAvailable()) return;
    
    try {
      window.adminDebug.addApiCall(url, method, request, response, duration);
    } catch (error) {
      console.warn('API call logging failed:', error);
    }
  }

  // Log user actions
  logUserAction(action, details = null) {
    if (!this.isEnabled || !this.isAvailable()) return;
    
    try {
      window.adminDebug.addUserAction(action, details);
    } catch (error) {
      console.warn('User action logging failed:', error);
    }
  }

  // Update app state in debug panel
  updateAppState(newState) {
    if (!this.isEnabled || !this.isAvailable()) return;
    
    try {
      window.adminDebug.setAppState(newState);
    } catch (error) {
      console.warn('App state update failed:', error);
    }
  }

  // Log component lifecycle events
  componentMounted(componentName, props = null) {
    this.system(`Component mounted: ${componentName}`, { 
      component: componentName, 
      props,
      timestamp: new Date().toISOString()
    });
  }

  componentUnmounted(componentName) {
    this.system(`Component unmounted: ${componentName}`, { 
      component: componentName,
      timestamp: new Date().toISOString()
    });
  }

  componentUpdated(componentName, changes = null) {
    this.system(`Component updated: ${componentName}`, { 
      component: componentName, 
      changes,
      timestamp: new Date().toISOString()
    });
  }

  // Log state changes
  stateChange(componentName, oldState, newState) {
    this.info(`State change in ${componentName}`, {
      component: componentName,
      oldState,
      newState,
      timestamp: new Date().toISOString()
    });
  }

  // Log form submissions
  formSubmission(formName, formData, result = null) {
    this.user(`Form submitted: ${formName}`, {
      form: formName,
      data: formData,
      result,
      timestamp: new Date().toISOString()
    });
  }

  // Log navigation events
  navigation(from, to, method = 'unknown') {
    this.user(`Navigation: ${from} → ${to}`, {
      from,
      to,
      method,
      timestamp: new Date().toISOString()
    });
  }

  // Log performance metrics
  performance(metric, value, context = null) {
    this.system(`Performance: ${metric}`, {
      metric,
      value,
      context,
      timestamp: new Date().toISOString()
    });
  }

  // Wrap fetch to automatically log API calls
  wrapFetch() {
    if (typeof window === 'undefined' || window.fetch._debugWrapped) return;

    const originalFetch = window.fetch;
    const logger = this;

    window.fetch = async function(...args) {
      const startTime = Date.now();
      const [url, options = {}] = args;
      const method = options.method || 'GET';
      
      logger.api(`API call started: ${method} ${url}`, {
        url,
        method,
        headers: options.headers,
        body: options.body
      });

      try {
        const response = await originalFetch(...args);
        const duration = Date.now() - startTime;
        
        // Clone response to read body without consuming it
        const responseClone = response.clone();
        let responseData = null;
        
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            responseData = await responseClone.json();
          } else {
            responseData = await responseClone.text();
          }
        } catch (e) {
          responseData = `Could not parse response: ${e.message}`;
        }

        logger.logApiCall(
          url.toString(),
          method,
          {
            headers: options.headers,
            body: options.body
          },
          {
            status: response.status,
            statusText: response.statusText,
            headers: Object.fromEntries(response.headers.entries()),
            data: responseData
          },
          duration
        );

        logger.api(`API call completed: ${method} ${url} (${response.status})`, {
          status: response.status,
          duration: `${duration}ms`
        });

        return response;
      } catch (error) {
        const duration = Date.now() - startTime;
        
        logger.logApiCall(
          url.toString(),
          method,
          {
            headers: options.headers,
            body: options.body
          },
          {
            error: error.message
          },
          duration
        );

        logger.error(`API call failed: ${method} ${url}`, {
          error: error.message,
          duration: `${duration}ms`
        });

        throw error;
      }
    };

    window.fetch._debugWrapped = true;
  }

  // Enable/disable logging
  enable() {
    this.isEnabled = true;
    this.system('Debug logging enabled');
  }

  disable() {
    this.system('Debug logging disabled');
    this.isEnabled = false;
  }

  // Get current status
  getStatus() {
    return {
      enabled: this.isEnabled,
      available: this.isAvailable(),
      timestamp: new Date().toISOString()
    };
  }
}

// Create and export singleton instance
const debugLogger = new DebugLogger();

// Auto-wrap fetch when module is loaded
if (typeof window !== 'undefined') {
  debugLogger.wrapFetch();
}

export default debugLogger; 